import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { IS_UPLOAD_REQUEST } from 'src/app/types/types';

@Component({
  selector: 'app-message-templates',
  templateUrl: './message-templates.component.html',
  styleUrls: ['./message-templates.component.scss']
})
export class MessageTemplatesComponent implements OnInit {

  loading = false;
  templates: any[] = [];
  afterCursor: string = null;
  beforeCursor: string = null;
  pageSize = 20;

  // Add-template form
  showForm = false;
  saving = false;
  uploadingImage = false;
  imageHandle = '';       // handle returned by the backend after uploading a header image
  imageFileName = '';
  form = {
    name: '',
    language: 'en_US',
    category: 'MARKETING',
    headerType: 'none',   // 'none' | 'text' | 'image'
    headerText: '',
    bodyText: '',
    bodyExample: '',      // comma-separated sample values for {{1}}, {{2}} ...
    footerText: ''
  };

  /** Distinct {{n}} placeholders used in the body. */
  get bodyVariableCount(): number {
    const matches = (this.form.bodyText || '').match(/\{\{\s*\d+\s*\}\}/g) || [];
    return new Set(matches.map(m => m.replace(/[^0-9]/g, ''))).size;
  }

  constructor(private http: HttpClient, private toastr: ToastrService, private router: Router) { }

  ngOnInit(): void {
    this.loadTemplates();
  }

  private get baseUrl() {
    return `${environment.serverUrl}/marketing/templates`;
  }

  loadTemplates(cursor: { after?: string, before?: string } = {}): void {
    this.loading = true;
    let url = `${this.baseUrl}?limit=${this.pageSize}`;
    if (cursor.after) { url += `&after=${cursor.after}`; }
    if (cursor.before) { url += `&before=${cursor.before}`; }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.templates = res?.data || [];
        this.afterCursor = res?.paging?.cursors?.after || null;
        this.beforeCursor = res?.paging?.cursors?.before || null;
        this.loading = false;
      },
      error: (e) => {
        this.loading = false;
        console.error('Load templates failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to load templates', 'Error');
      }
    });
  }

  onHeaderImageSelected(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) { return; }
    const formData = new FormData();
    formData.append('file', file);

    this.uploadingImage = true;
    this.http.post<any>(`${environment.serverUrl}/marketing/template-media`, formData, {
      context: new HttpContext().set(IS_UPLOAD_REQUEST, true)
    }).subscribe({
      next: (res) => {
        this.uploadingImage = false;
        this.imageHandle = res?.handle || '';
        this.imageFileName = file.name;
        this.toastr.success('Image uploaded', 'Ready');
        event.target.value = '';
      },
      error: (e) => {
        this.uploadingImage = false;
        event.target.value = '';
        console.error('Header image upload failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to upload image', 'Error');
      }
    });
  }

  nextPage(): void {
    if (this.afterCursor) { this.loadTemplates({ after: this.afterCursor }); }
  }

  prevPage(): void {
    if (this.beforeCursor) { this.loadTemplates({ before: this.beforeCursor }); }
  }

  toggleForm(): void {
    this.showForm = !this.showForm;
  }

  statusClass(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'APPROVED': return 'badge bg-success';
      case 'PENDING': return 'badge bg-warning';
      case 'REJECTED': return 'badge bg-danger';
      case 'PAUSED': return 'badge bg-secondary';
      default: return 'badge bg-light text-dark';
    }
  }

  templateBodyPreview(template: any): string {
    const comps = template?.components || [];
    const body = comps.find((c: any) => (c.type || '').toUpperCase() === 'BODY');
    return body?.text || '';
  }

  submitTemplate(): void {
    if (!this.form.name.trim()) { this.toastr.warning('Template name is required', 'Missing'); return; }
    if (!this.form.bodyText.trim()) { this.toastr.warning('Body text is required', 'Missing'); return; }

    const bodyText = this.form.bodyText.trim();
    const varCount = this.bodyVariableCount;

    // Meta rule: a variable cannot be the first or last thing in the body.
    if (varCount > 0 && (/^\{\{/.test(bodyText) || /\}\}$/.test(bodyText))) {
      this.toastr.warning('Body cannot start or end with a variable. Add text around {{1}} (e.g. "Hi {{1}}, welcome!").', 'Invalid template');
      return;
    }

    const components: any[] = [];
    if (this.form.headerType === 'text' && this.form.headerText.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: this.form.headerText.trim() });
    } else if (this.form.headerType === 'image') {
      if (!this.imageHandle) {
        this.toastr.warning('Please upload a header image first', 'Missing');
        return;
      }
      components.push({ type: 'HEADER', format: 'IMAGE', example: { header_handle: [this.imageHandle] } });
    }
    const bodyComponent: any = { type: 'BODY', text: bodyText };
    // Meta requires example values for every variable in the body.
    if (varCount > 0) {
      const samples = (this.form.bodyExample || '').split(',').map(s => s.trim()).filter(s => s.length > 0);
      const values: string[] = [];
      for (let i = 0; i < varCount; i++) {
        values.push(samples[i] || `Sample${i + 1}`);
      }
      bodyComponent.example = { body_text: [values] };
    }
    components.push(bodyComponent);

    if (this.form.footerText.trim()) {
      components.push({ type: 'FOOTER', text: this.form.footerText.trim() });
    }

    const payload = {
      name: this.form.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      language: this.form.language.trim(),
      category: this.form.category,
      components
    };

    this.saving = true;
    this.http.post<any>(this.baseUrl, payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success('Template submitted to WhatsApp for review', 'Success');
        this.showForm = false;
        this.form = { name: '', language: 'en_US', category: 'MARKETING', headerType: 'none', headerText: '', bodyText: '', bodyExample: '', footerText: '' };
        this.imageHandle = '';
        this.imageFileName = '';
        this.loadTemplates();
      },
      error: (e) => {
        this.saving = false;
        console.error('Create template failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to create template', 'Error');
      }
    });
  }

  setupCampaign(template?: any): void {
    const queryParams: any = {};
    if (template) {
      queryParams.templateName = template.name;
      queryParams.language = template.language;
    }
    this.router.navigate(['/whatsapp-bulk/campaign-setup'], { queryParams });
  }
}
